import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { MultiPhotoUploader } from '../components/MultiPhotoUploader';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { useAuth } from '../context/AuthContext';
import { useLocation as useUserLocation } from '../context/LocationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import { VinMissingWarningModal } from '../components/VinMissingWarningModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../lib/toast';
import {
  Car,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Shield,
  Lock,
  Gauge,
  Sparkles,
  DollarSign,
  Crown,
  Eye,
  MapPin,
  Camera,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const FUEL_OPTIONS = [
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'ev', label: 'EV' },
  { value: 'lpg', label: 'LPG' },
  { value: 'other', label: 'Other' },
];
const TRANS_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'automatic', label: 'Automatic' },
  { value: 'cvt', label: 'Continuously Variable' },
  { value: 'dct', label: 'Dual Clutch' },
];
const BODY_OPTIONS = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'hatchback', label: 'Hatchback' },
  { value: 'wagon', label: 'Wagon' },
  { value: 'suv', label: 'SUV' },
  { value: 'ute', label: 'Ute' },
  { value: 'van', label: 'Van' },
  { value: 'coupe', label: 'Coupe' },
  { value: 'convertible', label: 'Convertible' },
  { value: 'other', label: 'Other' },
];
const COLOUR_OPTIONS = [
  'White', 'Black', 'Grey', 'Silver', 'Blue', 'Red', 'Green', 'Yellow',
  'Orange', 'Brown', 'Beige', 'Gold', 'Purple', 'Bronze', 'Other',
];
const CYLINDER_OPTIONS = ['2', '3', '4', '5', '6', '8', '10', '12', 'Other'];
const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like new' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 1 - 1950 + 1 }, (_, i) => CURRENT_YEAR + 1 - i);
const WOVR_OPTIONS = [
  { v: 'none', l: 'Not written off' },
  { v: 'repairable', l: 'Repairable write-off' },
  { v: 'statutory', l: 'Statutory write-off' },
];
const SERVICE_HISTORY_OPTIONS = [
  { v: 'full', l: 'Full service history' },
  { v: 'partial', l: 'Partial' },
  { v: 'none', l: 'None' },
  { v: 'unknown', l: 'Unknown' },
];
const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

function detectState(locationName) {
  const upper = (locationName || '').toUpperCase();
  return AU_STATES.find((s) => upper.includes(s)) || null;
}

const STEP_LABELS = ['Vehicle & Listing', 'Additional Information (optional)'];

const TIER = {
  id: 'plus',
  label: 'Plus',
  priceLabel: '$20 one-off',
  expiryLabel: 'Indefinite listing',
  scoreCap: 100,
  photoCap: 20,
  descCap: 2000,
  ppsrLabel: 'PPSR check bundled',
  points: 200,
  features: [
    'Indefinite listing — no expiry',
    'Up to 20 photos + video',
    '2000 character description',
    'Quality score up to 100',
    'Featured placement — higher in search & browse',
    'PPSR check bundled (coming soon)',
    'Earn 200 points on purchase',
    'AI valuation (WANTD+ only)',
  ],
};

export default function AddVehicleListingPage() {
  const { user, api } = useAuth();
  const { location: userLocation } = useUserLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityBreakdown, setQualityBreakdown] = useState({});
  const [makesCatalog, setMakesCatalog] = useState({ cars: [], motorbikes: [], caravans: [], boats: [] });

  // Modal visibility
  const [vinWarningOpen, setVinWarningOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successItemId, setSuccessItemId] = useState(null);

  // Inline valuation state (rendered under the live preview)
  const [valuationLoading, setValuationLoading] = useState(false);
  const [valuationResult, setValuationResult] = useState(null);
  const [valuationError, setValuationError] = useState(null);
  const [aiDescribeText, setAiDescribeText] = useState('');
  const [aiDescribeLoading, setAiDescribeLoading] = useState(false);
  const [aiDescribeFilled, setAiDescribeFilled] = useState(null);

  // WANTD+ gating — matches ListLandingPage.js:14
  const isWantdPlus = !!(user?.is_premium || user?.is_trial_active);

  const [form, setForm] = useState({
    // Step 0 — Vehicle & Listing (core)
    subcategory: 'cars',
    make: '',
    make_other: '',
    model: '',
    variant: '',
    year: '',
    km: '',
    fuel_type: '',
    transmission: '',
    body_type: '',
    color: '',
    engine_size: '',
    cylinders: '',
    condition: '',
    title: '',
    description: '',
    price: '',
    location_name: userLocation?.name || '',
    location: userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : null,
    images: [],
    primary_image_index: 0,
    listing_tier: 'plus',
    is_eoi: false,
    is_firm: false,
    auto_terms_accepted: false,

    // Step 1 — Additional Information (optional fields that raise the quality score)
    vin: '',
    rego: '',
    rego_state: '',
    rego_expiry: '',
    build_date: '',
    compliance_date: '',
    owners_count: '',
    service_history: '',
    modifications: '',
    wovr_status: 'none',
    rwc: false,
    rwc_notes: '',
    gas_cert_notes: '',
    is_wrecks_parts: false,
    acknowledged_no_vin_ppsr_forfeit: false,
    ppsr_opt_in: false,
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const tier = TIER;
  const isCaravan = form.subcategory === 'caravans';
  const currentMakes = useMemo(
    () => (form.subcategory ? (makesCatalog[form.subcategory] || []) : []),
    [form.subcategory, makesCatalog]
  );

  // Load curated AU vehicle makes on mount (one fetch, cached via lru_cache server-side)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/inventory/vehicle-makes');
        if (!cancelled) setMakesCatalog(res.data || {});
      } catch {
        // silent — falls back to free text entry
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear valuation when inputs change so the user can re-run if they want
  useEffect(() => {
    if (valuationResult || valuationError) {
      setValuationResult(null);
      setValuationError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.make, form.make_other, form.model, form.year, form.km, form.condition]);

  // Auto-detect rego_state from location_name
  useEffect(() => {
    const detected = detectState(form.location_name);
    if (detected && !form.rego_state) {
      set('rego_state', detected);
    }
  }, [form.location_name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-suggest title from make/model/year
  useEffect(() => {
    if (!form.make && !form.model) return;
    const parts = [form.year, form.make, form.model, form.variant].filter(Boolean);
    const suggested = parts.join(' ');
    if (suggested && !form.title) {
      set('title', suggested);
    }
  }, [form.make, form.model, form.variant, form.year]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live quality-score preview (debounced 800ms) ─────────────────────────
  const previewTimer = useRef(null);
  const buildPreviewPayload = () => ({
    description: form.description || '',
    images: form.images || [],
    vehicle_details: {
      subcategory: form.subcategory || 'cars',
      make: form.make || null,
      model: form.model || null,
      year: form.year ? parseInt(form.year) : null,
      km: form.km ? parseInt(form.km) : null,
      fuel_type: form.fuel_type || null,
      transmission: form.transmission || null,
      body_type: form.body_type || null,
      color: form.color || null,
      variant: form.variant || null,
      engine_size: form.engine_size || null,
      cylinders: form.cylinders ? (form.cylinders === 'Other' ? form.cylinders : parseInt(form.cylinders)) : null,
      condition: form.condition || null,
      rego_expiry: form.rego_expiry || null,
      rwc: form.rwc,
      modifications: form.modifications || null,
      vin: form.vin || null,
      rego: form.rego || null,
      rego_state: form.rego_state || null,
      build_date: form.build_date || null,
      compliance_date: form.compliance_date || null,
      owners_count: form.owners_count ? parseInt(form.owners_count) : null,
      service_history: form.service_history || null,
      wovr_status: form.wovr_status || null,
      ppsr_opt_in: form.ppsr_opt_in,
      listing_tier: form.listing_tier,
    },
  });

  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const res = await api.post('/inventory/quality-score/preview', buildPreviewPayload());
        setQualityScore(res.data.score);
        setQualityBreakdown(res.data.breakdown || {});
      } catch {
        // preview is best-effort — don't toast on failure
      }
    }, 800);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [
    form.subcategory, form.make, form.model, form.variant, form.year, form.km,
    form.fuel_type, form.transmission, form.body_type, form.color,
    form.engine_size, form.cylinders, form.condition, form.rego_expiry,
    form.rwc, form.modifications,
    form.vin, form.rego, form.rego_state, form.build_date, form.compliance_date,
    form.owners_count, form.service_history, form.wovr_status, form.ppsr_opt_in,
    form.description, form.images.length, form.listing_tier,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step validation — Make/Model/Year are optional; Title is the anchor.
  function canAdvanceStep() {
    if (step === 0) {
      return !!(
        form.title.trim() &&
        (form.make || form.make_other) &&
        form.model.trim() &&
        form.variant.trim() &&
        form.year &&
        form.km &&
        form.transmission &&
        form.fuel_type &&
        form.body_type &&
        form.color &&
        form.engine_size.trim() &&
        form.cylinders &&
        form.condition &&
        (form.is_eoi || form.price !== '') &&
        form.images.length > 0 &&
        form.location &&
        form.auto_terms_accepted
      );
    }
    return true;
  }

  // Resolved Make value — "Other…" reveals an inline text input
  const resolvedMake = form.make === '__other__' ? form.make_other : form.make;

  // ── Submit handlers ──────────────────────────────────────────────────────
  async function createListing() {
    const price = form.is_eoi ? 0 : (parseFloat(form.price) || 0);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: 'vehicles',
      subcategory: form.subcategory,
      estimated_value: price,
      price: price,
      condition: form.condition || 'good',
      is_free: false,
      is_eoi: form.is_eoi,
      is_firm: form.is_firm,
      is_public: false,
      location: form.location,
      location_name: form.location_name,
      images: form.images,
      primary_image_index: form.primary_image_index,
      tags: [],
      vehicle_details: {
        subcategory: form.subcategory,
        make: resolvedMake || null,
        model: form.model || null,
        variant: form.variant || null,
        year: form.year ? parseInt(form.year) : null,
        km: form.km ? parseInt(form.km) : null,
        fuel_type: form.fuel_type || null,
        transmission: form.transmission || null,
        body_type: form.body_type || null,
        color: form.color || null,
        engine_size: form.engine_size || null,
        cylinders: form.cylinders ? (form.cylinders === 'Other' ? form.cylinders : parseInt(form.cylinders)) : null,
        vin: form.vin || null,
        rego: form.rego || null,
        rego_state: form.rego_state || null,
        rego_expiry: form.rego_expiry || null,
        build_date: form.build_date || null,
        compliance_date: form.compliance_date || null,
        owners_count: form.owners_count ? parseInt(form.owners_count) : null,
        service_history: form.service_history || null,
        modifications: form.modifications || null,
        wovr_status: form.wovr_status || null,
        rwc: form.rwc,
        rwc_notes: form.rwc_notes || null,
        gas_cert_notes: isCaravan ? (form.gas_cert_notes || null) : null,
        is_wrecks_parts: form.is_wrecks_parts,
        listing_tier: form.listing_tier,
        auto_terms_accepted: form.auto_terms_accepted,
        acknowledged_no_vin_ppsr_forfeit: form.acknowledged_no_vin_ppsr_forfeit,
        ppsr_opt_in: form.ppsr_opt_in,
      },
    };
    const res = await api.post('/inventory/', payload);
    return res.data;
  }

  async function handleAiDescribe() {
    setAiDescribeLoading(true);
    setAiDescribeFilled(null);
    try {
      const res = await api.post('/inventory/vehicle-ai-describe', {
        description: aiDescribeText,
        subcategory: form.subcategory || 'cars',
      });
      const data = res.data;
      const filled = [];
      setForm((f) => {
        const next = { ...f };
        if (data.suggested_title && !f.title) { next.title = data.suggested_title; filled.push('title'); }
        if (data.suggested_description && !f.description) { next.description = data.suggested_description; filled.push('description'); }
        if (data.suggested_make) {
          const match = currentMakes.find(m => m.toLowerCase() === data.suggested_make.toLowerCase());
          if (match && !f.make) { next.make = match; filled.push('make'); }
          else if (!f.make) { next.make = '__other__'; next.make_other = data.suggested_make; filled.push('make'); }
        }
        if (data.suggested_model && !f.model) { next.model = data.suggested_model; filled.push('model'); }
        if (data.suggested_variant && !f.variant) { next.variant = data.suggested_variant; filled.push('variant'); }
        if (data.suggested_year && data.suggested_year > 0 && !f.year) { next.year = String(data.suggested_year); filled.push('year'); }
        if (data.suggested_km && data.suggested_km > 0 && !f.km) { next.km = String(data.suggested_km); filled.push('km'); }
        if (data.suggested_body_type && !f.body_type) { next.body_type = data.suggested_body_type; filled.push('body_type'); }
        if (data.suggested_fuel_type && !f.fuel_type) { next.fuel_type = data.suggested_fuel_type; filled.push('fuel_type'); }
        if (data.suggested_transmission && !f.transmission) { next.transmission = data.suggested_transmission; filled.push('transmission'); }
        if (data.suggested_color && !f.color) {
          const raw = String(data.suggested_color).trim();
          const match = COLOUR_OPTIONS.find(c => c.toLowerCase() === raw.toLowerCase());
          next.color = match || 'Other';
          filled.push('colour');
        }
        if (data.suggested_engine_size && !f.engine_size) { next.engine_size = data.suggested_engine_size; filled.push('engine size'); }
        if (data.suggested_cylinders && !f.cylinders) {
          const cyls = String(data.suggested_cylinders);
          next.cylinders = CYLINDER_OPTIONS.includes(cyls) ? cyls : 'Other';
          filled.push('cylinders');
        }
        if (data.suggested_condition && !f.condition) { next.condition = data.suggested_condition; filled.push('condition'); }
        return next;
      });
      setAiDescribeFilled(filled);
      if (filled.length > 0) toast.success(`${filled.length} fields auto-filled — review and adjust as needed.`);
      else toast.info('No new fields to fill — your existing entries were kept.');
    } catch (err) {
      if (err.response?.status === 429) toast.error('Too many requests — try again shortly.');
      else toast.error('AI generation failed — fill in fields manually.');
    } finally {
      setAiDescribeLoading(false);
    }
  }

  async function handleSubmit() {
    if (!form.auto_terms_accepted) {
      toast.error('Please accept the Auto Terms & Conditions');
      setStep(0);
      return;
    }
    if (!form.title.trim()) {
      toast.error('Please enter a title');
      setStep(0);
      return;
    }
    if (!form.location) {
      toast.error('Please set a location');
      setStep(0);
      return;
    }
    if (!form.vin?.trim() && !form.acknowledged_no_vin_ppsr_forfeit) {
      setVinWarningOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const item = await createListing();

      try {
        await api.post(`/inventory/${item.id}/purchase-plus`);
        setSuccessItemId(item.id);
        setSuccessModalOpen(true);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(detail || 'Payment unsuccessful — your listing has been saved as a draft and won\u2019t be visible until payment succeeds. You can retry payment from My Items.');
        navigate('/my-items');
        return;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to submit listing. Please check all required fields.');
    } finally {
      setSubmitting(false);
    }
  }

  // Called by VIN warning modal "Continue without VIN"
  function handleContinueWithoutVin() {
    setVinWarningOpen(false);
    setForm((f) => ({ ...f, acknowledged_no_vin_ppsr_forfeit: true }));
    // Re-trigger submit on next tick — state update is async
    setTimeout(() => handleSubmit(), 0);
  }

  // Called by VIN warning modal "Add VIN now"
  function handleAddVinNow() {
    setVinWarningOpen(false);
    setStep(1);
    // Focus hint: scroll into view once step is rendered
    setTimeout(() => {
      const vinInput = document.querySelector('input[data-vin-input]');
      if (vinInput) vinInput.focus();
    }, 100);
  }

  async function runValuation() {
    if (!isWantdPlus) {
      toast.error('AI valuation is a WANTD+ feature — upgrade to unlock.');
      navigate('/premium');
      return;
    }
    if (!resolvedMake || !form.model || !form.year || !form.km) {
      toast.error('Fill Make, Model, Year and Odometer first.');
      return;
    }
    setValuationLoading(true);
    setValuationError(null);
    setValuationResult(null);
    try {
      const res = await api.post('/inventory/vehicle-valuation', {
        subcategory: form.subcategory || 'cars',
        make: resolvedMake,
        model: form.model,
        year: form.year ? Number(form.year) : null,
        km: form.km ? Number(form.km) : null,
        condition: form.condition || 'good',
        location_state: form.rego_state || detectState(form.location_name),
      });
      setValuationResult(res.data || {});
    } catch (err) {
      if (err?.response?.status === 403) setValuationError('WANTD+ subscription required.');
      else if (err?.response?.status === 429) setValuationError('Daily valuation limit reached.');
      else if (err?.response?.status === 503) setValuationError('Valuation service unavailable.');
      else setValuationError(err?.response?.data?.detail || 'Failed to generate valuation.');
    } finally {
      setValuationLoading(false);
    }
  }

  const scoreColor = useMemo(() => {
    if (qualityScore == null) return 'text-[var(--theme-text-muted)]';
    if (qualityScore >= 80) return 'text-[#059669]';
    if (qualityScore >= 50) return 'text-[#C9956C]';
    return 'text-[#D97706]';
  }, [qualityScore]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/auto/cars/list" className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)]">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Car className="w-5 h-5 text-[#1E3A5F]" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)]">List a Vehicle</h1>
            </div>
            <p className="text-sm text-[var(--theme-text-muted)]">
              Plus $20 · PPSR bundled · Classifieds-only
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                i < step ? 'bg-[#059669] text-white'
                : i === step ? 'bg-[#1E3A5F] text-white'
                : 'bg-[var(--theme-hover)] text-[var(--theme-text-muted)]'
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:block ${i === step ? 'text-[var(--theme-text-primary)] font-medium' : 'text-[var(--theme-text-muted)]'}`}>
                {label}
              </span>
              {i < STEP_LABELS.length - 1 && (
                <div className={`flex-1 h-px ${i < step ? 'bg-[#059669]' : 'bg-[var(--theme-border)]'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Live quality score (shown from Step 0 onwards) */}
        {qualityScore !== null && (
          <div className="mb-4 p-3 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)] flex items-center gap-3">
            <Gauge className={`w-5 h-5 ${scoreColor}`} />
            <div className="flex-1">
              <p className="text-xs text-[var(--theme-text-muted)]">Listing Quality Score (live preview)</p>
              <p className={`text-lg font-bold ${scoreColor}`}>
                {qualityScore}<span className="text-sm font-normal text-[var(--theme-text-muted)]">/{tier.scoreCap}</span>
              </p>
            </div>
            <p className="text-xs text-[var(--theme-text-muted)] text-right max-w-[180px]">
              Add more fields to reach 100
            </p>
          </div>
        )}

        {/* Form + Live Preview Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* Form Column */}
        <div className="lg:col-span-3">
        <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-6">

          {/* ── Step 0: Vehicle & Listing ── */}
          {step === 0 && (
            <div className="space-y-5">
              {/* ── Plus tier info ── */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-[#C9956C]/5 border border-[#C9956C]/20">
                <Shield className="w-4 h-4 text-[#C9956C] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[var(--theme-text-secondary)]">
                  <span className="font-bold text-[#C9956C]">Plus $20</span> — charged to your saved card on submit.
                  PPSR check runs automatically (coming soon). Indefinite listing, up to 20 photos, quality score up to 100.
                </p>
              </div>

              <div className="h-px bg-[var(--theme-border)]" />

              {/* AI Describe — quick-fill from freeform text */}
              <div className="p-4 rounded-lg border border-dashed border-[#1E3A5F]/30 bg-[#1E3A5F]/5">
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1.5 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-[#1E3A5F]" />
                  Describe your vehicle
                </label>
                <textarea
                  value={aiDescribeText}
                  onChange={(e) => { setAiDescribeText(e.target.value.slice(0, 500)); setAiDescribeFilled(null); }}
                  rows={2}
                  placeholder="e.g. 2015 white Toyota Camry, auto, 120k kms, sedan, petrol, one owner, full service history…"
                  className="w-full rounded-md border border-[var(--theme-border)] bg-[var(--theme-card)] px-3 py-2 text-sm text-[var(--theme-text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-[#1E3A5F]"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-[var(--theme-text-muted)]">
                    {aiDescribeFilled ? (
                      <span className="text-[#059669]">
                        <CheckCircle2 className="w-3 h-3 inline mb-0.5 mr-0.5" />
                        Filled {aiDescribeFilled.length} field{aiDescribeFilled.length !== 1 ? 's' : ''} — review below
                      </span>
                    ) : (
                      'Optional — or fill in the fields manually below'
                    )}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    disabled={aiDescribeText.trim().length < 10 || aiDescribeLoading}
                    onClick={handleAiDescribe}
                    className="bg-[#1E3A5F] text-white hover:bg-[#2D4A6F] flex items-center gap-1.5"
                  >
                    {aiDescribeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {aiDescribeLoading ? 'Generating…' : 'Generate Ad'}
                  </Button>
                </div>
              </div>

              {/* Title (anchor field — free-text fallback for "Other" vehicles) */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Listing Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => set('title', e.target.value.slice(0, 80))}
                  placeholder="e.g. 2018 Toyota Hilux SR5 — dual cab, low kms"
                  className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                />
                <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                  {form.title.length}/80 · This is the headline buyers see first.
                </p>
              </div>

              {/* Make */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Make <span className="text-red-500">*</span>
                </label>
                <Select
                  value={form.make}
                  onValueChange={(value) => set('make', value)}
                >
                  <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                    <SelectValue placeholder="Select make…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                    {currentMakes.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                    <SelectItem value="__other__">Other…</SelectItem>
                  </SelectContent>
                </Select>
                {form.make === '__other__' && (
                  <Input
                    value={form.make_other}
                    onChange={(e) => set('make_other', e.target.value)}
                    placeholder="Enter make"
                    className="mt-2 bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                )}
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Model <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.model}
                  onChange={(e) => set('model', e.target.value)}
                  placeholder="e.g. Camry, Hilux, Corolla"
                  className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                />
              </div>

              {/* Variant */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Variant <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.variant}
                  onChange={(e) => set('variant', e.target.value)}
                  placeholder="e.g. SR5, Atara S, Ascent Sport"
                  className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                />
              </div>

              {/* Year + Odometer */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.year}
                    onValueChange={(value) => set('year', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select year…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)] max-h-72">
                      {YEAR_OPTIONS.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Odometer (km) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={form.km}
                    onChange={(e) => set('km', e.target.value)}
                    placeholder="e.g. 85000"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                </div>
              </div>

              {/* Transmission + Fuel */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Transmission <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.transmission}
                    onValueChange={(value) => set('transmission', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select transmission…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {TRANS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Fuel Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.fuel_type}
                    onValueChange={(value) => set('fuel_type', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select fuel…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {FUEL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Body Type + Colour */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Body Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.body_type}
                    onValueChange={(value) => set('body_type', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select body…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {BODY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Colour <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.color}
                    onValueChange={(value) => set('color', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select colour…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {COLOUR_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Engine Size + Cylinders */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Engine Size <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={form.engine_size}
                    onChange={(e) => set('engine_size', e.target.value)}
                    placeholder="e.g. 2.5L, 250cc, Electric"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Cylinders <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.cylinders}
                    onValueChange={(value) => set('cylinders', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select cylinders…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {CYLINDER_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Condition + Registration Expiry */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Condition <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={form.condition}
                    onValueChange={(value) => set('condition', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select condition…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {CONDITION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Registration Expiry <span className="text-[var(--theme-text-muted)] font-normal">(leave blank if unregistered)</span>
                  </label>
                  <Input
                    type="date"
                    value={form.rego_expiry}
                    onChange={(e) => set('rego_expiry', e.target.value)}
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                </div>
              </div>

              <div className="h-px bg-[var(--theme-border)]" />

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Description{' '}
                  <span className="text-[var(--theme-text-muted)] font-normal">
                    ({form.description.length}/{tier.descCap})
                  </span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => set('description', e.target.value.slice(0, tier.descCap))}
                  rows={5}
                  placeholder="Tell buyers about the vehicle — service history, extras, condition, reason for sale. 200+ characters earns quality score points."
                  className="w-full rounded-md border border-[var(--theme-border)] bg-[var(--theme-hover)] px-3 py-2 text-sm text-[var(--theme-text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-[#1E3A5F]"
                />
              </div>

              {/* Asking Price + Firm/Flexible + Valuation + EOI (below) */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Asking Price {!form.is_eoi && <span className="text-red-500">*</span>}
                </label>
                {!form.is_eoi ? (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--theme-text-muted)]">$</span>
                      <Input
                        type="number"
                        min="0"
                        value={form.price}
                        onChange={(e) => set('price', e.target.value)}
                        placeholder="0"
                        className="pl-7 bg-[var(--theme-hover)] border-[var(--theme-border)]"
                      />
                    </div>
                    <div className="flex rounded-md border border-[var(--theme-border)] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => set('is_firm', false)}
                        className={`px-3 py-2 text-xs font-medium transition-colors ${
                          !form.is_firm
                            ? 'bg-[#1E3A5F] text-white'
                            : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
                        }`}
                      >
                        Flexible
                      </button>
                      <button
                        type="button"
                        onClick={() => set('is_firm', true)}
                        className={`px-3 py-2 text-xs font-medium transition-colors ${
                          form.is_firm
                            ? 'bg-[#1E3A5F] text-white'
                            : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'
                        }`}
                      >
                        Firm
                      </button>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={runValuation}
                      disabled={valuationLoading || !!valuationResult}
                      className={`flex items-center gap-1.5 whitespace-nowrap ${
                        valuationResult
                          ? 'border-[#059669] text-[#059669] bg-[#059669]/5'
                          : 'border-[#C9956C] text-[#C9956C] hover:bg-[#C9956C]/10'
                      }`}
                    >
                      {valuationLoading ? <Loader2 className="w-4 h-4 animate-spin" />
                        : valuationResult ? <CheckCircle2 className="w-4 h-4" />
                        : (isWantdPlus ? <DollarSign className="w-4 h-4" /> : <Lock className="w-4 h-4" />)}
                      <span>
                        {valuationLoading ? 'Valuing…'
                          : valuationResult ? 'Valuation complete'
                          : 'Get Valuation'}
                      </span>
                      {!isWantdPlus && !valuationLoading && !valuationResult && <Crown className="w-3 h-3 ml-0.5" />}
                    </Button>
                  </div>
                ) : (
                  <div className="px-3 py-2 rounded-md bg-[#1E3A5F]/5 border border-[#1E3A5F]/20 text-sm text-[var(--theme-text-secondary)]">
                    Price on request — buyers will contact you to make an offer.
                  </div>
                )}
                {!isWantdPlus && !form.is_eoi && (
                  <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                    <Crown className="w-3 h-3 inline mb-0.5 mr-0.5 text-[#C9956C]" />
                    AI valuation is a WANTD+ feature — searches recent AU listings for comparable prices.
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="checkbox"
                    checked={form.is_eoi}
                    onChange={(e) => { set('is_eoi', e.target.checked); if (e.target.checked) set('price', ''); }}
                    className="w-4 h-4 accent-[#1E3A5F]"
                    id="eoi-check"
                  />
                  <label htmlFor="eoi-check" className="text-sm text-[var(--theme-text-secondary)]">
                    Expression of Interest (EOI) &mdash; price on request
                  </label>
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  Location <span className="text-red-500">*</span>
                </label>
                <LocationAutocomplete
                  value={form.location_name}
                  onChange={(name) => set('location_name', name)}
                  onSelect={(loc) => {
                    set('location_name', loc.name);
                    set('location', { lat: loc.lat, lng: loc.lng });
                  }}
                  placeholder="Suburb or city"
                />
              </div>

              <div className="h-px bg-[var(--theme-border)]" />

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-2">
                  Photos (up to {tier.photoCap}) <span className="text-red-500">*</span>
                </label>
                <MultiPhotoUploader
                  images={form.images}
                  primaryIndex={form.primary_image_index}
                  onImagesChange={(imgs) => set('images', imgs)}
                  onPrimaryChange={(idx) => set('primary_image_index', idx)}
                  maxImages={tier.photoCap}
                  api={api}
                />
              </div>

              {/* Acknowledgement */}
              <div className={`p-4 rounded-lg border ${form.auto_terms_accepted ? 'border-[#059669] bg-[#059669]/5' : 'border-[var(--theme-border)]'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.auto_terms_accepted}
                    onChange={(e) => set('auto_terms_accepted', e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#1E3A5F]"
                  />
                  <span className="text-sm text-[var(--theme-text-secondary)]">
                    I have read and agree to the{' '}
                    <Link to="/auto/terms" target="_blank" className="text-[#1E3A5F] underline font-medium">
                      WANTD Auto Terms & Conditions
                    </Link>
                    . I confirm WANTD is a classifieds service and is not a party to any vehicle transaction. I have disclosed any write-off status, and I am not a licensed motor dealer listing dealer stock.{' '}
                    <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* ── Step 1: Additional Information (optional) ── */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-semibold text-[var(--theme-text-primary)]">
                Additional Information <span className="text-xs font-normal text-[var(--theme-text-muted)]">— optional</span>
              </h2>
              <p className="text-xs text-[var(--theme-text-muted)]">
                All fields optional. The more you provide, the higher your listing quality score. Plus listings use the VIN for the bundled PPSR check.
              </p>

              {/* VIN */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                  VIN <span className="text-[var(--theme-text-muted)] font-normal">(never shown publicly)</span>
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]" />
                  <Input
                    data-vin-input
                    value={form.vin}
                    onChange={(e) => set('vin', e.target.value.toUpperCase())}
                    placeholder="17-character Vehicle Identification Number"
                    maxLength={17}
                    className="pl-9 bg-[var(--theme-hover)] border-[var(--theme-border)] font-mono"
                  />
                </div>
                <label className={`flex items-center gap-2 mt-2 cursor-pointer text-sm ${form.vin?.trim() ? 'text-[var(--theme-text-secondary)]' : 'text-[var(--theme-text-muted)] opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={form.ppsr_opt_in}
                    onChange={(e) => set('ppsr_opt_in', e.target.checked)}
                    disabled={!form.vin?.trim()}
                    className="w-4 h-4 accent-[#1E3A5F]"
                  />
                  <span>
                    Include VIN for the bundled PPSR check <span className="text-[var(--theme-text-muted)] text-xs">(+10 quality score)</span>
                  </span>
                </label>
                <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                  Used only for PPSR checks on Plus listings. Not displayed on the public listing.
                </p>
              </div>

              {/* Rego + State */}
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Rego <span className="text-[var(--theme-text-muted)] font-normal">(masked publicly)</span>
                  </label>
                  <Input
                    value={form.rego}
                    onChange={(e) => set('rego', e.target.value.toUpperCase())}
                    placeholder="e.g. ABC123"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)] font-mono"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">State</label>
                  <Select
                    value={form.rego_state}
                    onValueChange={(value) => set('rego_state', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {AU_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Build / Compliance */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Build Date</label>
                  <Input
                    value={form.build_date}
                    onChange={(e) => set('build_date', e.target.value)}
                    placeholder="MM/YYYY"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Compliance Date</label>
                  <Input
                    value={form.compliance_date}
                    onChange={(e) => set('compliance_date', e.target.value)}
                    placeholder="MM/YYYY"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                </div>
              </div>

              {/* Owners + Service history */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Previous Owners</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.owners_count}
                    onChange={(e) => set('owners_count', e.target.value)}
                    placeholder="e.g. 2"
                    className="bg-[var(--theme-hover)] border-[var(--theme-border)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Service History</label>
                  <Select
                    value={form.service_history}
                    onValueChange={(value) => set('service_history', value)}
                  >
                    <SelectTrigger className="bg-[var(--theme-hover)] border-[var(--theme-border)]">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
                      {SERVICE_HISTORY_OPTIONS.map((o) => (
                        <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Modifications */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">Modifications (optional)</label>
                <Input
                  value={form.modifications}
                  onChange={(e) => set('modifications', e.target.value)}
                  placeholder="e.g. Aftermarket exhaust, lowered, engineered"
                  className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                />
              </div>

              {/* WOVR */}
              <div>
                <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-2">Write-off Status (WOVR)</label>
                <div className="flex gap-2">
                  {WOVR_OPTIONS.map((o) => (
                    <button
                      key={o.v}
                      onClick={() => set('wovr_status', o.v)}
                      className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                        form.wovr_status === o.v
                          ? 'border-[#1E3A5F] bg-[#1E3A5F]/10 text-[#1E3A5F]'
                          : 'border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:border-[#1E3A5F]/50'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                  Sellers are required to disclose written-off vehicles. Misrepresentation may breach consumer law.
                </p>
              </div>

              {/* RWC */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--theme-border)]">
                <div>
                  <p className="text-sm font-medium text-[var(--theme-text-primary)]">Roadworthy Certificate (RWC)</p>
                  <p className="text-xs text-[var(--theme-text-muted)]">Has a current RWC / safety certificate</p>
                </div>
                <Switch checked={form.rwc} onCheckedChange={(val) => set('rwc', val)} />
              </div>
              {form.rwc && (
                <Input
                  value={form.rwc_notes}
                  onChange={(e) => set('rwc_notes', e.target.value)}
                  placeholder="RWC details (optional)"
                  className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                />
              )}

              {/* Gas cert — caravans only */}
              {isCaravan && (
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Gas Certificate Notes
                  </label>
                  <Input
                    value={form.gas_cert_notes}
                    onChange={(e) => set('gas_cert_notes', e.target.value)}
                    placeholder="Gas compliance cert details (strongly recommended for caravans)"
                    className="bg-[var(--theme-bg)] border-[var(--theme-border)]"
                  />
                </div>
              )}

              {/* Wrecks & Parts flag */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--theme-border)]">
                <div>
                  <p className="text-xs font-medium text-[var(--theme-text-primary)]">Wrecks & Parts</p>
                </div>
                <Switch
                  checked={form.is_wrecks_parts}
                  onCheckedChange={(val) => set('is_wrecks_parts', val)}
                />
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[var(--theme-border)]">
            <Button
              variant="outline"
              onClick={() => (step === 0 ? navigate('/auto/cars/list') : setStep((s) => s - 1))}
              disabled={submitting}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>

            <div className="flex items-center gap-2">
              {step < 1 ? (
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canAdvanceStep()}
                  className="bg-[#1E3A5F] hover:bg-[#2d5a8e] text-white"
                >
                  Add additional info
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !form.title.trim() || !form.auto_terms_accepted}
                  className="bg-[#C9956C] hover:bg-[#B8845B] text-white"
                >
                  {submitting ? 'Submitting…' : 'Pay $20 & Publish'}
                </Button>
              )}
            </div>
          </div>
        </div>
        </div>{/* /Form Column */}

        {/* Live Preview Column — desktop only, matches /list-item */}
        <div className="hidden lg:block lg:col-span-2">
          <div className="sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-[var(--theme-text-secondary)]" />
              <span className="text-sm font-medium text-[var(--theme-text-secondary)]">Live Preview</span>
            </div>

            {/* Preview Card — mirrors CatalogCard at rest */}
            <div className="rounded-xl border border-[var(--theme-border)] overflow-hidden bg-[var(--theme-card)] max-w-[300px]">
              {/* Preview Image */}
              <div className="relative aspect-square overflow-hidden bg-[var(--theme-hover)]">
                {form.images.length > 0 ? (
                  <img
                    src={
                      typeof form.images[form.primary_image_index] === 'object'
                        ? form.images[form.primary_image_index]?.url
                        : form.images[form.primary_image_index]
                    }
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[var(--theme-text-muted)]">
                    <Camera className="w-10 h-10 mb-2 opacity-30" />
                    <span className="text-xs opacity-50">Add photos to preview</span>
                  </div>
                )}
                {form.images.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                    +{form.images.length - 1}
                  </div>
                )}
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#C9956C] text-white text-[10px] font-bold uppercase tracking-wider">
                  Plus
                </div>
              </div>

              {/* Preview Content */}
              <div className="p-3">
                <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2 min-h-[2.5rem] leading-tight">
                  {form.title || 'Your listing title'}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-base font-bold text-[#059669]">
                    {form.is_eoi
                      ? <span className="bg-[#1E3A5F] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">EOI</span>
                      : form.price !== '' && form.price != null
                        ? `$${Number(form.price).toLocaleString()}`
                        : '$0'}
                  </span>
                  {form.is_firm && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] uppercase tracking-wider">
                      Firm
                    </span>
                  )}
                </div>

                {/* Vehicle meta line — year / make / model / km */}
                {(form.year || resolvedMake || form.model || form.variant || form.km) && (
                  <div className="text-[11px] text-[var(--theme-text-secondary)] mt-1 line-clamp-1">
                    {[form.year, resolvedMake, form.model, form.variant].filter(Boolean).join(' ')}
                    {form.km ? ` · ${Number(form.km).toLocaleString()} km` : ''}
                  </div>
                )}

                {/* Secondary badges */}
                {(form.transmission || form.fuel_type || form.body_type) && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {form.transmission && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] capitalize">
                        {TRANS_OPTIONS.find((o) => o.value === form.transmission)?.label || form.transmission}
                      </span>
                    )}
                    {form.fuel_type && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] capitalize">
                        {FUEL_OPTIONS.find((o) => o.value === form.fuel_type)?.label || form.fuel_type}
                      </span>
                    )}
                    {form.body_type && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] capitalize">
                        {BODY_OPTIONS.find((o) => o.value === form.body_type)?.label || form.body_type}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-1 mt-2 text-[var(--theme-text-muted)]">
                  <MapPin className="w-3 h-3" />
                  <span className="text-[11px] truncate">
                    {form.location_name || 'Your location'}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-[10px] text-[var(--theme-text-muted)] mt-3 text-center">
              This is how your listing will appear in the catalog
            </p>

            {/* AI valuation tile — desktop only, populated by runValuation() */}
            {(valuationLoading || valuationResult || valuationError) && (
              <div className="mt-4 rounded-xl border border-[var(--theme-border)] bg-[var(--theme-card)] p-4 max-w-[300px]">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-[#D97706]" />
                  <span className="text-sm font-medium text-[var(--theme-text-secondary)]">AI valuation</span>
                </div>

                {valuationLoading && (
                  <div className="py-4 flex flex-col items-center gap-2 text-[var(--theme-text-secondary)]">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-xs">Estimating…</span>
                    <span className="text-[10px] text-[var(--theme-text-muted)]">Usually under 30 seconds.</span>
                  </div>
                )}

                {valuationError && !valuationLoading && (
                  <div className="flex items-start gap-2 p-2 rounded-md bg-red-500/10 border border-red-500/30 text-xs text-red-400">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{valuationError}</span>
                  </div>
                )}

                {valuationResult && !valuationLoading && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-1.5 text-center">
                      <div className="p-2 rounded-md bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                        <div className="text-[9px] text-[var(--theme-text-muted)] uppercase">Low</div>
                        <div className="text-sm font-bold text-[var(--theme-text-primary)]">
                          ${Number(valuationResult.estimated_low_aud || 0).toLocaleString()}
                        </div>
                      </div>
                      <div className="p-2 rounded-md bg-[#1E3A5F] border border-[#2d5a8e]">
                        <div className="text-[9px] text-white/70 uppercase">Mid</div>
                        <div className="text-sm font-bold text-white">
                          ${Number(valuationResult.estimated_mid_aud || 0).toLocaleString()}
                        </div>
                      </div>
                      <div className="p-2 rounded-md bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                        <div className="text-[9px] text-[var(--theme-text-muted)] uppercase">High</div>
                        <div className="text-sm font-bold text-[var(--theme-text-primary)]">
                          ${Number(valuationResult.estimated_high_aud || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {valuationResult.reasoning && (
                      <p className="text-[11px] text-[var(--theme-text-secondary)] leading-relaxed">
                        {valuationResult.reasoning}
                      </p>
                    )}

                    <p className="text-[9px] text-[var(--theme-text-muted)] italic">
                      AI estimate based on recent AU private-sale listings. Informational only — not a certified appraisal.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>{/* /Form + Live Preview Grid */}
      </div>

      {/* VIN missing warning (Plus + empty VIN) */}
      <VinMissingWarningModal
        open={vinWarningOpen}
        onAddVin={handleAddVinNow}
        onContinue={handleContinueWithoutVin}
        onClose={() => setVinWarningOpen(false)}
      />

      {/* Payment success modal — replaces toast, persists until user acts */}
      <Dialog open={successModalOpen} onOpenChange={() => { /* lock until user picks an action */ }}>
        <DialogContent className="bg-[var(--theme-card)] border-[var(--theme-border)] max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#059669]" />
              <DialogTitle className="text-[var(--theme-text-primary)]">Payment successful — your Plus listing is live</DialogTitle>
            </div>
          </DialogHeader>

          {/* Ad preview — mirrors the live-preview card */}
          <div className="rounded-xl border border-[var(--theme-border)] overflow-hidden bg-[var(--theme-card)]">
            <div className="relative aspect-square overflow-hidden bg-[var(--theme-hover)]">
              {form.images.length > 0 ? (
                <img
                  src={
                    typeof form.images[form.primary_image_index] === 'object'
                      ? form.images[form.primary_image_index]?.url
                      : form.images[form.primary_image_index]
                  }
                  alt="Listing preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--theme-text-muted)]">
                  <Camera className="w-8 h-8 opacity-30" />
                </div>
              )}
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#C9956C] text-white text-[10px] font-bold uppercase tracking-wider">
                Plus
              </div>
            </div>
            <div className="p-3">
              <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2">
                {form.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-base font-bold text-[#059669]">
                  {form.is_eoi
                    ? <span className="bg-[#1E3A5F] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">EOI</span>
                    : form.price !== '' && form.price != null
                      ? `$${Number(form.price).toLocaleString()}`
                      : '$0'}
                </span>
                {form.is_firm && !form.is_eoi && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] uppercase tracking-wider">
                    Firm
                  </span>
                )}
              </div>
              {(form.year || resolvedMake || form.model || form.variant) && (
                <div className="text-[11px] text-[var(--theme-text-secondary)] mt-1 line-clamp-1">
                  {[form.year, resolvedMake, form.model, form.variant].filter(Boolean).join(' ')}
                  {form.km ? ` · ${Number(form.km).toLocaleString()} km` : ''}
                </div>
              )}
              <div className="flex items-center gap-1 mt-2 text-[var(--theme-text-muted)]">
                <MapPin className="w-3 h-3" />
                <span className="text-[11px] truncate">{form.location_name}</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-[var(--theme-text-muted)]">
            A receipt has been saved to your Billing & Invoices. The PPSR check will run in the background and appear on your listing once complete.
          </p>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSuccessModalOpen(false);
                navigate('/my-items');
              }}
              className="border-[var(--theme-border)]"
            >
              OK — go to My Items
            </Button>
            <Button
              onClick={() => {
                setSuccessModalOpen(false);
                navigate(`/auto/${form.subcategory || 'cars'}/${successItemId}`);
              }}
              className="bg-[#1E3A5F] hover:bg-[#2d5a8e] text-white"
            >
              View my listing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
